import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from "recharts";
import { formatNumber } from "../common/Misc";

const MixBarChart = ({ data }) => {
  const { contract_value, work_remaining, actual_paid, outstanding_amount } =
    data;

  const LABELS = {
    actual_paid: "Actual Paid",
    outstanding_amount: "Outstanding Amount",
    work_remaining: "Work Remaining",
  };

  const chartData = [
    {
      name: "Contract",
      actual_paid,
      outstanding_amount,
      work_remaining,
    },
  ];

  const COLORS = {
    actual_paid: "#6ee7b7", // dark pastel green
    outstanding_amount: "#fde68a", // dark pastel yellow
    work_remaining: "#fca5a5", // dark pastel red
  };

  // ✅ Custom Tooltip (Inline CSS)
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          {payload.map((item) => (
            <div
              key={item.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "4px",
              }}
            >
              <div
                style={{
                  background: item.color,
                  height: "12px",
                  width: "12px",
                  borderRadius: "2px",
                }}
              ></div>
              <div>
                <strong>{LABELS[item.name]}:</strong> $
                {formatNumber(item.value)}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // ✅ Custom Legend (Inline CSS)
  const CustomLegend = ({ payload }) => {
    return (
      <div
        style={{
          display: "flex",
          gap: "16px",
          fontSize: "14px",
          paddingLeft: "30px",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "5px"
        }}
      >
        {payload.map((entry) => (
          <div
            key={entry.value}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
          >
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                backgroundColor: entry.color,
                borderRadius: "2px",
              }}
            />
            <span>{LABELS[entry.value] || entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={100}>
      <BarChart
        layout="vertical"
        data={chartData}
        margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
      >
        <XAxis type="number" domain={[0, contract_value]} tick={false}/>
        <YAxis type="category" dataKey="name" />
        <Tooltip content={<CustomTooltip />} />
        <Legend content={<CustomLegend />} />

        <Bar dataKey="actual_paid" stackId="total" fill={COLORS.actual_paid} />
        <Bar
          dataKey="outstanding_amount"
          stackId="total"
          fill={COLORS.outstanding_amount}
        />
        <Bar
          dataKey="work_remaining"
          stackId="total"
          fill={COLORS.work_remaining}
        />
        {/* Work Remaining Marker */}
        <ReferenceLine x={contract_value} stroke="#000000" strokeWidth={2}>
          <Label
            content={({ viewBox }) => {
              const { x, y } = viewBox;
              return (
                <text
                  x={x - 10} // adjust to move left of the line
                  y={y - 7} // above the chart
                  textAnchor="end" // right-align the text
                  fill="#000000"
                  fontSize={16}
                >
                  {`Contract Value ($${formatNumber(contract_value)})`}
                </text>
              );
            }}
          />
        </ReferenceLine>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default MixBarChart;
